import React, { useEffect, useRef, useState } from 'react';
import { TaskStats as TaskStatsType } from '../types';
import styles from '../../../../styles/TaskManagement.module.css';

interface TaskStatsProps {
  stats: TaskStatsType;
  isWorkMode?: boolean;
  activePersonalTab?: 'assigned' | 'self_assigned' | 'approvals' | 'initiatives' | 'requests';
  activeWorkTab?: 'tasks' | 'templates';
}

const AnimatedNumber: React.FC<{ value: number; duration?: number }> = ({ value, duration = 1500 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      setIsAnimating(true);
      const startValue = prevValueRef.current;
      const endValue = value;
      const startTime = Date.now();

      const animate = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(startValue + (endValue - startValue) * easeOut);

        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setDisplayValue(endValue);
          setIsAnimating(false);
          prevValueRef.current = endValue;
        }
      };

      requestAnimationFrame(animate);
    } else if (!isAnimating) {
      setDisplayValue(value);
    }
  }, [value, duration, isAnimating]);

  return <span>{displayValue.toLocaleString()}</span>;
};

const TaskStats: React.FC<TaskStatsProps> = ({ stats, isWorkMode = false, activePersonalTab = 'assigned', activeWorkTab = 'tasks' }) => {
  // Get labels based on mode and tab
  const getTotalLabel = () => {
    if (isWorkMode) {
      return activeWorkTab === 'templates' ? 'Total Templates' : 'Total Tasks Created';
    }
    switch (activePersonalTab) {
      case 'assigned':
        return 'Tasks Assigned to Me';
      case 'self_assigned':
        return 'Self-Assigned Instances';
      case 'approvals':
        return 'Tasks Pending Approval';
      case 'initiatives':
        return 'Total Initiatives';
      default:
        return 'Total Tasks';
    }
  };

  const getPointsLabel = () => {
    if (isWorkMode) return 'Points Distributed';
    switch (activePersonalTab) {
      case 'assigned':
        return 'Points Earned';
      case 'self_assigned':
        return 'Points from Instances';
      case 'approvals':
        return 'Tasks to Review';
      case 'requests':
        return 'Pre-Approval Requests';
      case 'initiatives':
        return 'Points Earned';
      default:
        return 'Points Earned';
    }
  };

  const getInProgressLabel = () => {
    if (isWorkMode) return 'In Progress';
    switch (activePersonalTab) {
      case 'approvals':
      case 'requests':
        return 'Under Review';
      case 'initiatives':
        return 'Active Tasks'; // Or "In Progress"
      default:
        return 'In Progress';
    }
  };

  // For approvals/requests tab, show different stat cards
  if (!isWorkMode && (activePersonalTab === 'approvals' || activePersonalTab === 'requests')) {
    const pendingApprovals = stats.inProgress; // Tasks pending review (hack: using inProgress field)
    const approvedCount = stats.completed; // Approved tasks (hack: using completed field)
    const rejectedCount = stats.pointsEarned; // Rejected/others (hack: using pointsEarned field)

    return (
      <div className={styles.taskStats}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            <AnimatedNumber value={stats.totalTasks} />
          </div>
          <div className={styles.statLabel}>Total Requests</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            <AnimatedNumber value={pendingApprovals} />
          </div>
          <div className={styles.statLabel}>Pending Review</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            <AnimatedNumber value={approvedCount} />
          </div>
          <div className={styles.statLabel}>Approved</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            <AnimatedNumber value={rejectedCount} />
          </div>
          <div className={styles.statLabel}>Rejected/Others</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.taskStats}>
      <div className={styles.statCard}>
        <div className={styles.statValue}>
          <AnimatedNumber value={stats.totalTasks} />
        </div>
        <div className={styles.statLabel}>
          {getTotalLabel()}
        </div>
      </div>

      {!(isWorkMode && activeWorkTab === 'templates') && (
        <>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              <AnimatedNumber value={stats.inProgress} />
            </div>
            <div className={styles.statLabel}>{getInProgressLabel()}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              <AnimatedNumber value={stats.completed} />
            </div>
            <div className={styles.statLabel}>Completed</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              <AnimatedNumber value={activePersonalTab === 'approvals' ? stats.totalTasks : stats.pointsEarned} />
            </div>
            <div className={styles.statLabel}>
              {getPointsLabel()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TaskStats;

